// 📄 pages/Proyectos/components/InvitarMiembro.tsx
// Pedido explícito: la mayoría de las veces se invita a alguien que YA es
// miembro de la organización dueña del proyecto, así que el selector
// arranca mostrando esa lista (nombre + email) en vez de pedir escribir
// el email a mano — mucho más rápido y sin margen de error de tipeo.
// "Otro (invitar por email)" al final de la lista vuelve al flujo viejo
// (útil para invitar a alguien externo a la organización).
import React, { useEffect, useState } from 'react';
import { UserPlus, RefreshCw, UserCog, Users } from 'lucide-react';
import { findPlatformUserByEmail } from '../../../services/userService.ts';
import { getOrgMembers, OrgMemberConNombre } from '../../../services/organizationService.ts';
import { addMember, createProjectMemberAccount, notificarAgregadoAProyecto, ProjectRole, ROLE_OPTIONS } from '../../../services/projectService.ts';
import { useProject } from '../../../context/ProjectContext.tsx';
import { useAuth } from '../../../context/AuthContext.tsx';
import { getDisplayName } from '../../../utils/displayName.ts';

const OTRO = '__otro__';

interface Props {
  projectId: string;
  // Org dueña del proyecto — si falta (proyecto legado sin orgId), el
  // selector cae directo al flujo de "invitar por email", sin lista.
  orgId?: string;
  // Uids ya miembros de ESTE proyecto — se excluyen de la lista para no
  // ofrecer invitar de nuevo a alguien que ya está.
  miembrosActualesIds: string[];
}

export default function InvitarMiembro({ projectId, orgId, miembrosActualesIds }: Props) {
  const [miembrosOrg, setMiembrosOrg] = useState<OrgMemberConNombre[]>([]);
  const [cargandoOrg, setCargandoOrg] = useState(true);

  const [seleccion, setSeleccion] = useState<string>(OTRO);
  const [email, setEmail] = useState('');
  const [rol, setRol] = useState<ProjectRole>('tecnico');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Solo se muestra cuando ya buscamos el email (rama "Otro") y no existe
  // ninguna cuenta.
  const [showCreateAccount, setShowCreateAccount] = useState(false);
  const [nombreCompleto, setNombreCompleto] = useState('');
  // Para el aviso por correo: nombre del proyecto y de quién invita.
  const { project } = useProject();
  const { user, profile } = useAuth();
  const invitadoPor = user ? getDisplayName(user, profile) : 'Un miembro del equipo';
  const avisar = (correo: string) => notificarAgregadoAProyecto(correo, project?.nombre || 'un proyecto', projectId, invitadoPor);

  useEffect(() => {
    if (!orgId) { setCargandoOrg(false); return; }
    setCargandoOrg(true);
    getOrgMembers(orgId)
      .then((lista) => setMiembrosOrg(lista.filter((m) => !miembrosActualesIds.includes(m.id))))
      .catch((err) => console.error('Error cargando miembros de la organización:', err))
      .finally(() => setCargandoOrg(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const esOtro = seleccion === OTRO;

  const resetCreateAccountForm = () => {
    setShowCreateAccount(false);
    setNombreCompleto('');
  };

  const resetTodo = () => {
    setSeleccion(OTRO);
    setEmail('');
    resetCreateAccountForm();
  };

  const handleInvitar = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    // Rama rápida: eligió a alguien de la lista de la organización — ya
    // sabemos que existe (uid + email conocidos), no hace falta buscar.
    if (!esOtro) {
      const miembro = miembrosOrg.find((m) => m.id === seleccion);
      if (!miembro) return;
      setLoading(true);
      try {
        await addMember(projectId, { userId: miembro.id, rol, email: miembro.email });
        await avisar(miembro.email);
        setSuccess(`${miembro.displayName} fue agregado al proyecto y le avisamos por correo.`);
        setMiembrosOrg((prev) => prev.filter((m) => m.id !== miembro.id));
        resetTodo();
      } catch (err: any) {
        setError(err.message || 'Error al invitar al miembro.');
      } finally {
        setLoading(false);
      }
      return;
    }

    // Rama "Otro": mismo flujo de siempre, buscar por email.
    setLoading(true);
    try {
      const found = await findPlatformUserByEmail(email);
      if (found) {
        await addMember(projectId, { userId: found.uid, rol, email: found.email });
        await avisar(found.email);
        setSuccess(`${found.email} ya tenía cuenta: fue agregado al proyecto y le avisamos por correo.`);
        setEmail('');
        resetCreateAccountForm();
      } else {
        // No existe: pedimos los datos para crear la cuenta.
        setShowCreateAccount(true);
      }
    } catch (err: any) {
      setError(err.message || 'Error al invitar al miembro.');
    } finally {
      setLoading(false);
    }
  };

  const handleCrearCuenta = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!nombreCompleto.trim()) { setError('El nombre completo es obligatorio.'); return; }

    setLoading(true);
    try {
      const { correoEnviado } = await createProjectMemberAccount({
        projectId,
        email,
        rol,
        nombreCompleto: nombreCompleto.trim()
      });
      setSuccess(correoEnviado
        ? `Se creó la cuenta de ${email} y fue agregado al proyecto. Le llegará un correo para crear su contraseña.`
        : `Se creó la cuenta de ${email} y fue agregado al proyecto, pero no se pudo enviar el correo. Pídele que entre con "¿Olvidaste tu contraseña?" usando su correo.`);
      setEmail('');
      resetCreateAccountForm();
    } catch (err: any) {
      setError(err.message || 'Error al crear la cuenta del miembro.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-4">
      <h3 className="text-sm font-semibold text-gray-300 flex items-center gap-2">
        <UserPlus className="w-4 h-4 text-blue-400" /> Invitar miembro
      </h3>

      <form onSubmit={handleInvitar} className="space-y-3">
        <div className="flex flex-col sm:flex-row gap-3">
          {cargandoOrg ? (
            <div className="flex-1 min-w-0 flex items-center gap-2 text-xs text-gray-500 bg-zinc-800 border border-white/10 rounded-lg px-3 py-2">
              <RefreshCw className="w-3.5 h-3.5 animate-spin" /> Cargando miembros de la organización...
            </div>
          ) : (
            <select
              value={seleccion}
              onChange={(e) => { setSeleccion(e.target.value); setEmail(''); resetCreateAccountForm(); }}
              disabled={showCreateAccount}
              className="flex-1 min-w-0 bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm disabled:opacity-50"
            >
              {miembrosOrg.length > 0 && (
                <optgroup label="Miembros de la organización">
                  {miembrosOrg.map((m) => (
                    <option key={m.id} value={m.id}>{m.displayName} ({m.email})</option>
                  ))}
                </optgroup>
              )}
              <option value={OTRO}>Otro (invitar por email)</option>
            </select>
          )}
          <select
            value={rol}
            onChange={(e) => setRol(e.target.value as ProjectRole)}
            className="flex-shrink-0 bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm"
          >
            {ROLE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          {!showCreateAccount && !esOtro && (
            <button
              type="submit"
              disabled={loading}
              className="flex-shrink-0 flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 disabled:opacity-50 px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-all"
            >
              {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <><Users className="w-4 h-4" /> Invitar</>}
            </button>
          )}
        </div>

        {esOtro && (
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); resetCreateAccountForm(); }}
              placeholder="email@empresa.com"
              className="flex-1 min-w-0 bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm"
              required
              disabled={showCreateAccount}
            />
            {!showCreateAccount && (
              <button
                type="submit"
                disabled={loading}
                className="flex-shrink-0 flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 disabled:opacity-50 px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-all"
              >
                {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Invitar'}
              </button>
            )}
          </div>
        )}
      </form>

      {showCreateAccount && (
        <form onSubmit={handleCrearCuenta} className="space-y-3 bg-black/20 border border-white/10 rounded-xl p-4">
          <p className="text-xs text-amber-400 flex items-center gap-1.5">
            <UserCog className="w-3.5 h-3.5" /> No existe ninguna cuenta con {email}. Escribe su nombre: le llegará un correo de bienvenida para crear su propia contraseña.
          </p>

          <div>
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
              Nombre completo
            </label>
            <input
              value={nombreCompleto}
              onChange={(e) => setNombreCompleto(e.target.value)}
              placeholder="Nombre y apellido"
              className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm"
              required
            />
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={resetCreateAccountForm}
              className="flex-1 bg-white/5 hover:bg-white/10 border border-white/10 py-2 rounded-lg text-sm font-semibold transition-all"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 disabled:opacity-50 py-2 rounded-lg text-sm font-semibold transition-all"
            >
              {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Crear cuenta y agregar'}
            </button>
          </div>
        </form>
      )}

      {error && <p className="text-red-400 text-xs">{error}</p>}
      {success && <p className="text-emerald-400 text-xs">{success}</p>}
    </div>
  );
}
